import React, { useEffect, useRef } from 'react';

/**
 * 비연 소개 블록 전용 — 에디토리얼 톤의 정돈된 씬 인트로.
 * 2초 유지 → 크로스페이드로 두 개의 선언문이 부드럽게 교차한다.
 * 배경은 낮은 대비의 드리프트 도트와 옅은 오빗 링으로, 다른 섹션의
 * 타이포·여백 리듬과 이질감 없이 이어지도록 설계했다.
 */

const FONT_STACK =
  "'Pretendard', 'Apple SD Gothic Neo', 'Malgun Gothic', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const MONO_FONT_STACK =
  "'JetBrains Mono', 'SF Mono', 'IBM Plex Mono', 'Fira Code', Menlo, Consolas, 'Courier New', monospace";

/* 사이트 전역 토큰과 동일한 팔레트 — 에디토리얼 슬레이트 + 단일 액센트 */
const COLOR_BG = '#faf7f0';
const COLOR_TEXT_LEAD = '#0f172a';
const COLOR_TEXT_BODY = '#475569';
const COLOR_ACCENT = '#5a42d4';
const COLOR_LABEL = '#5a42d4';
const COLOR_RULE = 'rgba(90, 66, 212, 0.32)';
const COLOR_DOT_DIM = 'rgba(15, 23, 42, 0.16)';
const COLOR_DRIFT = 'rgba(90, 66, 212,';

/** Scene 1: 비전 */
const FULL_INTRO_LEAD = '현실과 가상을 초연결하는 몰입형 생태계';
const FULL_INTRO_BODY =
  'BIIYEON은 공간의 물리적 한계를 지우고, 사람과 예술, 브랜드가 시공간의 제약 없이 교감하는 새로운 차원의 경험을 설계합니다.';
const SCENE_A_LABEL = '01 / Vision';
const SCENE_A_HIGHLIGHTS = [{ phrase: '새로운 차원의 경험', color: COLOR_ACCENT }];

/** Scene 2: 실행 */
const FULL_INTRO_LEAD_ALT = '세상을 연결하는 XR 크리에이티브 랩';
const FULL_INTRO_BODY_ALT =
  '우리는 단순한 기술 실험에 머물지 않습니다. 공간 컴퓨팅과 원천 기술 연구를 바탕으로, 일상 속 몰입의 기준을 바꾸는 혁신적인 플랫폼을 끊임없이 세상에 선보입니다.';
const SCENE_B_LABEL = '02 / Practice';
const SCENE_B_HIGHLIGHTS = [{ phrase: '일상 속 몰입의 기준을 바꾸는', color: COLOR_ACCENT }];

const INTRO_HOLD_MS = 2800;
const INTRO_CROSS_MS = 560;

function easeInOutCubic(u) {
  return u < 0.5 ? 4 * u * u * u : 1 - (-2 * u + 2) ** 3 / 2;
}

function getIntroSlideMix(nowMs) {
  const cycle = INTRO_HOLD_MS * 2 + INTRO_CROSS_MS * 2;
  const loop = nowMs % cycle;
  const h = INTRO_HOLD_MS;
  const c = INTRO_CROSS_MS;
  if (loop < h) return { a: 1, b: 0, dyA: 0, dyB: 0 };
  if (loop < h + c) {
    const u = easeInOutCubic((loop - h) / c);
    return { a: 1 - u, b: u, dyA: -10 * u, dyB: 10 * (1 - u) };
  }
  if (loop < h + c + h) return { a: 0, b: 1, dyA: 0, dyB: 0 };
  const u = easeInOutCubic((loop - h - c - h) / c);
  return { a: u, b: 1 - u, dyA: 10 * (1 - u), dyB: -10 * u };
}

/** 결정적 유사 난수 — 서버/클라이언트 간 동일한 도트 분포 유지 */
function prand(i, seed) {
  const x = Math.sin(i * seed + seed * 1.37) * 43758.5453;
  return x - Math.floor(x);
}

/** 드리프트 도트 필드 — 공간 속에 떠 있는 옅은 입자 (ARBackground 톤과 호응) */
const DRIFT_COUNT = 22;
const DRIFT_DOTS = Array.from({ length: DRIFT_COUNT }, (_, i) => ({
  x: prand(i, 12.9898),
  y: prand(i, 78.233),
  r: 0.7 + prand(i, 37.719) * 1.6,
  phase: i * 0.47,
  speed: 0.11 + prand(i, 51.042) * 0.22,
  ampX: 10 + prand(i, 93.141) * 14,
  ampY: 8 + prand(i, 27.182) * 12,
  baseAlpha: 0.05 + prand(i, 63.771) * 0.12,
}));

function segmentLineWithHighlights(line, highlights) {
  if (!highlights || !highlights.length) return [{ text: line, hl: false }];
  const sorted = [...highlights].sort((a, b) => b.phrase.length - a.phrase.length);
  const out = [];
  let i = 0;
  while (i < line.length) {
    let hit = null;
    for (const h of sorted) {
      if (line.startsWith(h.phrase, i)) {
        hit = h;
        break;
      }
    }
    if (hit) {
      out.push({ text: hit.phrase, color: hit.color, hl: true });
      i += hit.phrase.length;
    } else {
      let next = line.length;
      for (const h of sorted) {
        const idx = line.indexOf(h.phrase, i);
        if (idx > i && idx < next) next = idx;
      }
      out.push({ text: line.slice(i, next), hl: false });
      i = next;
    }
  }
  return out;
}

function wrapTextLines(ctx, text, maxWidth) {
  const lines = [];
  let cur = '';
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    const test = cur + c;
    if (ctx.measureText(test).width > maxWidth && cur.length > 0) {
      lines.push(cur);
      cur = c;
    } else {
      cur = test;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

/** 배경 드리프트 도트 — 느리게 진동하며 옅은 생기감을 만든다 */
function drawDriftDots(ctx, w, h, t, mx, my) {
  for (const d of DRIFT_DOTS) {
    const dx = Math.sin(t * d.speed + d.phase) * d.ampX;
    const dy = Math.cos(t * d.speed * 0.78 + d.phase * 1.1) * d.ampY;
    const px = d.x * w + dx + mx * 9;
    const py = d.y * h + dy + my * 7;
    const pulse = 0.55 + 0.45 * Math.sin(t * d.speed * 1.6 + d.phase);
    const alpha = d.baseAlpha * (0.6 + 0.4 * pulse);
    ctx.fillStyle = `${COLOR_DRIFT} ${alpha})`;
    ctx.beginPath();
    ctx.arc(px, py, d.r, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** 소프트 오빗 링 — 중앙을 느슨하게 감싸는 정적 가이드 */
function drawSoftOrbitRings(ctx, w, h, t, mx, my) {
  const cx = w * 0.5 + mx * 14;
  const cy = h * 0.5 + my * 9;
  const maxR = Math.min(w, h) * 0.62;
  const n = 3;
  ctx.save();
  for (let i = 0; i < n; i++) {
    const rBase = maxR * (0.42 + i * 0.18);
    const breath = Math.sin(t * 0.26 + i * 0.9) * 3;
    const r = rBase + breath;
    const alpha = 0.045 + (1 - i / n) * 0.025;
    ctx.strokeStyle = `rgba(90, 66, 212, ${alpha})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

/** 씬 라벨 — 모노 대문자·넓은 트래킹, 좌우 짧은 룰이 양옆으로 나간다 (section-eyebrow와 같은 언어) */
function drawSceneLabel(ctx, cx, y, size, label) {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `600 ${size}px ${MONO_FONT_STACK}`;
  if ('letterSpacing' in ctx) ctx.letterSpacing = '3px';

  ctx.fillStyle = COLOR_LABEL;
  ctx.fillText(label, cx, y);

  const textWidth = ctx.measureText(label).width;
  const ruleLen = Math.max(24, size * 2.6);
  const ruleGap = Math.max(10, size * 0.95);
  const halfText = textWidth / 2;
  ctx.strokeStyle = COLOR_RULE;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - halfText - ruleGap, y);
  ctx.lineTo(cx - halfText - ruleGap - ruleLen, y);
  ctx.moveTo(cx + halfText + ruleGap, y);
  ctx.lineTo(cx + halfText + ruleGap + ruleLen, y);
  ctx.stroke();
  ctx.restore();
}

/** 평면 텍스트 라인 — 3D 시어/섀도우 없이 깔끔하게, 하이라이트 어구만 컬러 강조 */
function drawTextLine(ctx, segments, cx, y, fontSize, weight, defaultColor, shiftX, shiftY) {
  if (!segments.length) return;
  ctx.save();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  if ('fontKerning' in ctx) ctx.fontKerning = 'normal';
  const numericWeight = parseInt(weight, 10) || 400;
  if ('letterSpacing' in ctx) {
    ctx.letterSpacing =
      numericWeight >= 600 ? `${Math.round(fontSize * -0.022)}px` : `${Math.round(fontSize * 0.012)}px`;
  }

  const widths = [];
  let tw = 0;
  for (const seg of segments) {
    const segWeight = seg.hl ? '700' : weight;
    ctx.font = `${segWeight} ${fontSize}px ${FONT_STACK}`;
    const mw = ctx.measureText(seg.text).width;
    widths.push(mw);
    tw += mw;
  }

  let x = cx - tw / 2 + shiftX;
  const yy = y + shiftY;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const segWeight = seg.hl ? '700' : weight;
    ctx.font = `${segWeight} ${fontSize}px ${FONT_STACK}`;
    ctx.fillStyle = seg.hl ? seg.color : defaultColor;
    ctx.fillText(seg.text, x, yy);
    x += widths[i];
  }
  ctx.restore();
}

/** 본문 블록 조판 — 라벨 + 리드 + 바디를 세로 중앙에 정렬하고, 폭을 초과하면 축소 반복 */
function drawFullIntro(ctx, w, h, mx, my, sceneLabel, leadSource, bodySource, highlights, yShift) {
  const cx = w * 0.5;
  const textMax = Math.max(160, w * 0.78);
  const labelSize = Math.max(10, Math.min(12.5, w * 0.014));
  const labelBlock = labelSize * 3.6;
  let leadSize = Math.max(19, Math.min(30, w * 0.042));
  let bodySize = Math.max(13.5, Math.min(17.5, w * 0.0248));
  const leadLh = 1.42;
  const bodyLh = 1.78;

  for (let attempt = 0; attempt < 12; attempt++) {
    ctx.font = `700 ${leadSize}px ${FONT_STACK}`;
    const leadLines = wrapTextLines(ctx, leadSource, textMax);
    ctx.font = `400 ${bodySize}px ${FONT_STACK}`;
    const bodyLines = wrapTextLines(ctx, bodySource, textMax);

    const paraGap = leadLines.length && bodyLines.length ? leadSize * 0.85 : 0;
    const totalH =
      labelBlock +
      leadLines.length * leadSize * leadLh +
      paraGap +
      bodyLines.length * bodySize * bodyLh;

    if (totalH > h * 0.82 && attempt < 11) {
      leadSize *= 0.93;
      bodySize *= 0.93;
      continue;
    }

    let yTop = (h - totalH) / 2 + yShift;
    const shiftX = mx * 4;
    const shiftY = my * 2;

    drawSceneLabel(ctx, cx + shiftX * 0.6, yTop + labelSize * 0.5, labelSize, sceneLabel);
    yTop += labelBlock;

    for (let i = 0; i < leadLines.length; i++) {
      const yMid = yTop + leadSize / 2;
      const segs = segmentLineWithHighlights(leadLines[i], highlights);
      drawTextLine(ctx, segs, cx, yMid, leadSize, '700', COLOR_TEXT_LEAD, shiftX, shiftY);
      yTop += leadSize * leadLh;
    }

    yTop += paraGap;

    for (let j = 0; j < bodyLines.length; j++) {
      const yMid = yTop + bodySize / 2;
      const segs = segmentLineWithHighlights(bodyLines[j], highlights);
      drawTextLine(ctx, segs, cx, yMid, bodySize, '400', COLOR_TEXT_BODY, shiftX * 0.7, shiftY * 0.7);
      yTop += bodySize * bodyLh;
    }

    break;
  }
}

/** 하단 씬 인디케이터 — 활성 씬 점은 액센트, 비활성은 희미한 슬레이트 */
function drawSceneIndicator(ctx, w, h, mix) {
  const cy = h - Math.max(26, h * 0.085);
  const cx = w * 0.5;
  const gap = 18;
  const r = 3.4;

  const aHot = mix.a;
  const bHot = mix.b;

  const aColor = `rgba(90, 66, 212, ${0.2 + 0.78 * aHot})`;
  const bColor = `rgba(90, 66, 212, ${0.2 + 0.78 * bHot})`;

  const drawDot = (x, fill, hot) => {
    ctx.save();
    if (hot > 0.55) {
      ctx.shadowColor = 'rgba(90, 66, 212, 0.35)';
      ctx.shadowBlur = 8;
    }
    ctx.fillStyle = hot < 0.2 ? COLOR_DOT_DIM : fill;
    ctx.beginPath();
    ctx.arc(x, cy, r + hot * 0.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  drawDot(cx - gap / 2, aColor, aHot);
  drawDot(cx + gap / 2, bColor, bHot);
}

const AboutDepthXRVisual = () => {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const visibleRef = useRef(false);
  const mouseRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;

    const ctx = canvas.getContext('2d', { alpha: false });
    let rafId = 0;
    let cancelled = false;

    const step = () => {
      if (cancelled) return;
      if (!visibleRef.current) {
        rafId = 0;
        return;
      }
      draw();
      rafId = requestAnimationFrame(step);
    };

    const startLoopIfNeeded = () => {
      if (cancelled || rafId !== 0) return;
      rafId = requestAnimationFrame(step);
    };

    const resize = () => {
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      if (w < 2 || h < 2) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const onPointer = (e) => {
      const r = wrap.getBoundingClientRect();
      if (r.width < 1 || r.height < 1) return;
      mouseRef.current.tx = ((e.clientX - r.left) / r.width) * 2 - 1;
      mouseRef.current.ty = ((e.clientY - r.top) / r.height) * 2 - 1;
    };

    const onLeave = () => {
      mouseRef.current.tx = 0;
      mouseRef.current.ty = 0;
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        visibleRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          resize();
          startLoopIfNeeded();
        }
      },
      { root: null, threshold: 0.06 }
    );
    io.observe(wrap);
    const syncVisible = () => {
      const r = wrap.getBoundingClientRect();
      visibleRef.current = r.bottom > 0 && r.top < window.innerHeight;
    };
    syncVisible();

    resize();
    window.addEventListener('resize', resize);
    wrap.addEventListener('pointermove', onPointer, { passive: true });
    wrap.addEventListener('pointerleave', onLeave);

    let fontsDone = null;
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      fontsDone = document.fonts.ready.then(() => {
        if (!cancelled) resize();
      });
    }

    function draw() {
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      if (w < 2 || h < 2) return;

      const m = mouseRef.current;
      m.x += (m.tx - m.x) * 0.07;
      m.y += (m.ty - m.y) * 0.07;
      const mx = m.x;
      const my = m.y;
      timeRef.current += 0.018;
      const t = timeRef.current;

      /* 1. 베이스 배경색 (아이보리) */
      ctx.fillStyle = COLOR_BG;
      ctx.fillRect(0, 0, w, h);

      /* 2. 옅은 라디얼 글로우 — 마우스 위치를 따라 살짝 이동 */
      const rg = ctx.createRadialGradient(
        w * 0.5 + mx * 32,
        h * 0.46 + my * 20,
        0,
        w * 0.5,
        h * 0.5,
        Math.max(w, h) * 0.72
      );
      rg.addColorStop(0, 'rgba(90, 66, 212, 0.07)');
      rg.addColorStop(0.55, 'rgba(90, 66, 212, 0.02)');
      rg.addColorStop(1, 'rgba(90, 66, 212, 0)');
      ctx.fillStyle = rg;
      ctx.fillRect(0, 0, w, h);

      /* 3. 배경 오빗 링과 드리프트 도트 */
      drawSoftOrbitRings(ctx, w, h, t, mx, my);
      drawDriftDots(ctx, w, h, t, mx, my);

      /* 4. 두 씬의 본문 — 크로스페이드 루프 */
      const mix = getIntroSlideMix(performance.now());
      if (mix.a > 0.008) {
        ctx.save();
        ctx.globalAlpha = mix.a;
        drawFullIntro(ctx, w, h, mx, my, SCENE_A_LABEL, FULL_INTRO_LEAD, FULL_INTRO_BODY, SCENE_A_HIGHLIGHTS, mix.dyA);
        ctx.restore();
      }
      if (mix.b > 0.008) {
        ctx.save();
        ctx.globalAlpha = mix.b;
        drawFullIntro(
          ctx,
          w,
          h,
          mx,
          my,
          SCENE_B_LABEL,
          FULL_INTRO_LEAD_ALT,
          FULL_INTRO_BODY_ALT,
          SCENE_B_HIGHLIGHTS,
          mix.dyB
        );
        ctx.restore();
      }

      /* 5. 하단 씬 인디케이터 */
      drawSceneIndicator(ctx, w, h, mix);
    }

    startLoopIfNeeded();

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      io.disconnect();
      window.removeEventListener('resize', resize);
      wrap.removeEventListener('pointermove', onPointer);
      wrap.removeEventListener('pointerleave', onLeave);
      if (fontsDone && typeof fontsDone.catch === 'function') {
        fontsDone.catch(() => {});
      }
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className="home-about-depth__visual home-about-depth__visual--xr"
      aria-hidden="true"
    >
      <canvas ref={canvasRef} className="home-about-depth__xr-canvas" />
    </div>
  );
};

export default AboutDepthXRVisual;
